import React, { Suspense, lazy, useEffect, useState } from 'react'
import { useAppDispatch } from '../../store'
import { Box } from '@mui/material'
import LoadingPage from '../../pages/LoadingPage'
import { type ApplyFiltersAndDispatchParams } from '../../components/CustomTable/types'
import ROUTES from '../../consts/paths'
import { type CustomBreadcrumbT } from '../../utils/BreadcrumbDataPreparation'
import { divisionList, getLoading } from '../../store/slices/courseCatalogSlice/selectors'
import { getDivisionCoursesApi } from '../../store/slices/courseCatalogSlice/api'
import CreateAndEditCourseCatalog from './CreateNewCourseCatalog'
import CoursesCatalogTopbarActions from './CoursesCatalogTopbarActions'
import DeleteCourseCatalog from './DeleteCourseCatalog'
import PageHeader from '../../components/PageHeader'
import PAGE_TITLES from '../../consts/titles'
import { exportCsv } from '../../utils/exportCsv'
import { notification } from '../../components/Notifications'
import { DivisionManager } from '../../APIManager'
import { useMutation } from '@tanstack/react-query'

const AppTable = lazy(async () => await import('../../components/CustomTable'))

interface ExtendedParams extends ApplyFiltersAndDispatchParams {
  categoryName?: string
}

const CourseCatalog: React.FC = () => {
  const [params, setParams] = useState<ExtendedParams>({ categoryName: 'Course' })
  const loading = getLoading()
  const courseCatalogList = divisionList()
  const dispatch = useAppDispatch()

  const breadcrumbPrefixList: CustomBreadcrumbT[] = [{ title: 'Course Catalog', path: ROUTES.COURSE_CATALOG }]

  useEffect(() => {
    void dispatch(getDivisionCoursesApi({ params }))
  }, [params])

  const { mutate: exportCourseCatalogCSV, isPending: exportCSVLoading } = useMutation({
    mutationFn: DivisionManager.exportCourseCatalogCSV,
    onSuccess: (response) => {
      const data = response?.data
      if (data) {
        exportCsv({ data, headers: response?.headers })
      }
    },
    onError: (error: any) => {
      notification(error?.message ?? 'Export csv failed', { variant: 'error' })
    }
  })

  const handleExport = (): void => {
    const exportParams = {
      search: params?.search || '',
      sort: params?.sort || undefined,
      categoryName: params?.categoryName || 'Course'
    }
    exportCourseCatalogCSV(exportParams)
  }

  return (
    <Box display='flex' flexDirection='column' gap={6}>
      <Suspense fallback={<LoadingPage />}>
        <PageHeader title={PAGE_TITLES.COURSE_CATALOG} breadcrumb={breadcrumbPrefixList} />
        <AppTable
          list={courseCatalogList}
          deleteAction={<DeleteCourseCatalog />}
          customTopbarAction={<CoursesCatalogTopbarActions params={params} />}
          editAction={<CreateAndEditCourseCatalog params={params} />}
          exportCsv={handleExport}
          exportButtonProps={{ loading: exportCSVLoading, disabled: exportCSVLoading }}
          loading={loading}
          featureName="Course"
          params={params}
          setParams={setParams}
        />
      </Suspense>
    </Box>
  )
}

export default CourseCatalog
